"use client";

import React, { useState, useMemo, useEffect } from "react";
import {
  Mail,
  Calendar,
  GraduationCap,
  TrendingUp,
  Sparkles,
  ShieldCheck,
  FileText,
  Search,
  Filter,
  X,
  ChevronRight,
  ChevronLeft,
  ExternalLink,
  Check,
  Copy,
  RefreshCw,
  Download,
  Eye,
  MousePointerClick,
  AlertCircle,
  Clock,
  ArrowUpDown,
  Send,
  Building,
  User,
  Paperclip,
  CheckCircle2,
  AlertTriangle,
  RotateCw,
} from "lucide-react";
import { API_BASE, fetchWithAuth } from "@/lib/auth";

export interface UserRef {
  id: string;
  name: string | null;
  email: string | null;
  role: string | null;
}

export interface EmailLogRow {
  id: string;
  recipient_email: string;
  recipient_name: string | null;
  user: UserRef | null;
  purpose: string;
  purpose_label: string;
  stream?: string;
  stream_label?: string;
  is_test?: boolean;
  audience: string | null;
  meeting_id: string | null;
  event_title: string | null;
  group_name: string | null;
  group_type: string | null;
  triggered_by: string | null;
  trigger: string | null;
  subject: string | null;
  from_email: string | null;
  provider?: string;
  status: string;
  opened?: boolean;
  opened_at?: string | null;
  open_count?: number;
  clicked?: boolean;
  clicked_at?: string | null;
  click_link?: string | null;
  bounced?: boolean;
  bounce_reason?: string | null;
  delivered?: boolean;
  delivered_at?: string | null;
  institute_name?: string | null;
  designation?: string | null;
  attachment?: string | null;
  partner_id?: number | null;
  extra?: Record<string, any>;
  message_id: string | null;
  error_detail: string | null;
  created_at: string;
}

export interface EmailStreamSummary {
  stream: string;
  label: string;
  icon: string;
  description: string;
  total: number;
  all_time_total?: number;
  sent: number;
  opened: number;
  clicked: number;
  failed: number;
  open_rate: number;
  click_rate: number;
}

export interface EmailSummary {
  range: string;
  totals: {
    total: number;
    all_time_total?: number;
    sent: number;
    skipped: number;
    held?: number;
    failed: number;
    opened?: number;
    open_rate?: number;
    clicked?: number;
    click_rate?: number;
  };
  by_stream?: EmailStreamSummary[];
  by_purpose: {
    purpose: string;
    label: string;
    stream?: string;
    stream_label?: string;
    is_test?: boolean;
    count: number;
    all_time_count?: number;
    sent: number;
    opened?: number;
    open_rate?: number;
    clicked?: number;
    click_rate?: number;
    failed: number;
  }[];
  by_audience: { audience: string; count: number }[];
  draft_edits: number;
}

export interface DraftEditRow {
  id: string;
  meeting_id: string;
  user: UserRef | null;
  field_path: string;
  old_value: string | null;
  new_value: string | null;
  source_route: string | null;
  created_at: string;
}

interface StaffEmailInsightsProps {
  summary: EmailSummary | null;
  initialLogs: EmailLogRow[];
  draftEdits: DraftEditRow[];
  range: string;
  onRangeChange: (r: string) => void;
  onRefresh: () => void;
  /**
   * Shared Platform Insights window as a query string (range or start/end, tz,
   * internal filter). When given, log fetches use it and the local range
   * buttons are hidden — the page's filter bar owns the date range.
   */
  windowQuery?: string;
}

export const STREAM_CONFIG: Record<
  string,
  { label: string; icon: React.ComponentType<{ className?: string }>; color: string; bg: string; border: string; accent: string }
> = {
  all: {
    label: "All Streams",
    icon: Mail,
    color: "text-slate-800",
    bg: "bg-slate-100",
    border: "border-slate-300",
    accent: "#003d45",
  },
  events: {
    label: "Events & Chapters",
    icon: Calendar,
    color: "text-teal-700",
    bg: "bg-teal-50",
    border: "border-teal-200",
    accent: "#007a8a",
  },
  academic: {
    label: "Academic & Scholarships",
    icon: GraduationCap,
    color: "text-blue-700",
    bg: "bg-blue-50",
    border: "border-blue-200",
    accent: "#2563eb",
  },
  investment_challenge: {
    label: "Investment Challenge",
    icon: TrendingUp,
    color: "text-emerald-700",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    accent: "#059669",
  },
  candidates: {
    label: "Candidates & Curriculum",
    icon: Sparkles,
    color: "text-amber-700",
    bg: "bg-amber-50",
    border: "border-amber-200",
    accent: "#d97706",
  },
  admissions: {
    label: "Admissions Committee",
    icon: ShieldCheck,
    color: "text-indigo-700",
    bg: "bg-indigo-50",
    border: "border-indigo-200",
    accent: "#4f46e5",
  },
  governance: {
    label: "Surveys & Governance",
    icon: FileText,
    color: "text-purple-700",
    bg: "bg-purple-50",
    border: "border-purple-200",
    accent: "#7c3aed",
  },
};

/**
 * Complete catalog of all known campaign dispatches mapped directly by stream.
 * Guarantees that campaign filters in Events, Candidates, Admissions, Investment Challenge,
 * Academic, and Governance are ALWAYS fully populated and never empty!
 */
export const ALL_CAMPAIGNS_BY_STREAM: Record<string, { value: string; label: string }[]> = {
  events: [
    { value: "event_phase2", label: "Event Announcement (Phase 2)" },
    { value: "event_update", label: "Event Update" },
    { value: "event_phase4", label: "Event Recap (Phase 4)" },
    { value: "pune_community_meeting_general", label: "Pune Community Meeting (General)" },
    { value: "pune_community_meeting_candidate", label: "Pune Community Meeting (Candidates)" },
    { value: "pune_community_meeting_faculty", label: "Pune Community Meeting (Faculty)" },
    { value: "pune_meeting_general_test", label: "Pune Meeting (General Test)" },
    { value: "pune_meeting_candidate_test", label: "Pune Meeting (Candidate Test)" },
    { value: "pune_meeting_faculty_test", label: "Pune Meeting (Faculty Test)" },
  ],
  investment_challenge: [
    { value: "investment_challenge_candidate", label: "IC Candidate Announcement" },
    { value: "investment_challenge_trade_requirement", label: "IC Trade Requirement" },
    { value: "investment_challenge_inactivity_reminder", label: "IC Inactivity Reminder" },
    { value: "investment_challenge_profile_update", label: "IC Profile Update" },
    { value: "investment_challenge_test", label: "IC Test Email" },
  ],
  candidates: [
    { value: "cmt_curriculum_office_hours_dec2026", label: "CMT Curriculum Office Hours" },
    { value: "dec2026_office_hours_setting_a_trend", label: "Follow-up: Office Hours & Setting A Trend" },
    { value: "l3_passed_journey_video", label: "CMT Level III Journey Video" },
    { value: "l3_setting_a_trend_guidelines", label: "CMT Level III Setting A Trend" },
    { value: "l3_passed_journey_video_test", label: "CMT Level III Journey Video (Test)" },
    { value: "l3_setting_a_trend_guidelines_test", label: "CMT Level III Setting A Trend (Test)" },
    { value: "cmt_curriculum_office_hours_dec2026_test", label: "CMT Curriculum Office Hours (Test)" },
    { value: "dec2026_office_hours_setting_a_trend_test", label: "Follow-up: Office Hours & Setting A Trend (Test)" },
    { value: "setting_a_trend_registrants", label: "Setting A Trend: Registrant Reminder" },
    { value: "setting_a_trend_registrants_test", label: "Setting A Trend: Registrant Reminder (Test)" },
    { value: "setting_a_trend_feedback", label: "Setting A Trend: Session Feedback" },
    { value: "setting_a_trend_feedback_test", label: "Setting A Trend: Session Feedback (Test)" },
    { value: "office_hours_oct2026_reminder", label: "Office Hours: October Live Reminder" },
    { value: "office_hours_oct2026_reminder_test", label: "Office Hours: October Live Reminder (Test)" },
  ],
  academic: [
    { value: "app_scholarship_registration_dec2026", label: "APP Scholarship Registration (Dec 2026)" },
    { value: "investment_challenge_faculty", label: "IC Faculty Announcement" },
    { value: "app_scholarship_registration_dec2026_test", label: "APP Scholarship Registration (Dec 2026 Test)" },
  ],
  admissions: [
    { value: "admissions_digest_member", label: "Admissions Digest (Members)" },
    { value: "admissions_digest_chair", label: "Admissions Digest (Chairs)" },
    { value: "admissions_review_request", label: "Admissions Review Request" },
    { value: "admissions_weekly_digest", label: "Admissions Weekly Digest" },
    { value: "admissions_digest_board", label: "Admissions Digest (Board)" },
    { value: "admissions_followup_staff", label: "Admissions Staff Follow-up" },
    { value: "admissions_review_reminder", label: "Admissions Review Reminder" },
    { value: "admissions_majority_escalation", label: "Admissions Majority Escalation" },
  ],
  governance: [
    { value: "member_value_survey_reminder", label: "Member Value Survey Reminder" },
    { value: "coc_welcome", label: "Code of Conduct Welcome" },
    { value: "member_value_survey_reminder_test", label: "Member Value Survey Reminder (Test)" },
  ],
};

/**
 * Robust stream resolution: guarantees that every email is accurately
 * mapped to its proper stream even if backend returned without it.
 */
export function resolveStream(purpose?: string | null, backendStream?: string | null): string {
  if (backendStream && backendStream !== "general" && STREAM_CONFIG[backendStream]) {
    return backendStream;
  }
  const p = (purpose || "").toLowerCase();
  if (p.includes("event") || p.includes("meeting") || p.includes("pune")) return "events";
  if (p.includes("scholarship") || (p.includes("investment_challenge") && p.includes("faculty"))) return "academic";
  if (p.includes("investment_challenge")) return "investment_challenge";
  if (p.includes("curriculum") || p.includes("office_hours") || p.includes("l3")) return "candidates";
  if (p.includes("admission")) return "admissions";
  if (p.includes("survey") || p.includes("coc")) return "governance";
  return "events";
}

function relTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function StaffEmailInsights({
  summary,
  initialLogs,
  draftEdits,
  range,
  onRangeChange,
  onRefresh,
  windowQuery,
}: StaffEmailInsightsProps) {
  // Sync state
  const [syncDays, setSyncDays] = useState<number>(14);
  const [syncingBrevo, setSyncingBrevo] = useState<boolean>(false);
  const [syncMsg, setSyncMsg] = useState<{ text: string; error?: boolean } | null>(null);
  const [singleSyncingId, setSingleSyncingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Email rows state
  const [rows, setRows] = useState<EmailLogRow[]>(initialLogs);
  const [loadingStream, setLoadingStream] = useState<boolean>(false);

  useEffect(() => {
    setRows(initialLogs);
  }, [initialLogs]);

  // Drawer / Inspection state
  const [selectedEmail, setSelectedEmail] = useState<EmailLogRow | null>(null);

  // Filters State - Simplified, Intuitive
  const [activeStream, setActiveStream] = useState<string>("all");
  const [purposeFilter, setPurposeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [audienceFilter, setAudienceFilter] = useState<string>("all");
  const [testFilter, setTestFilter] = useState<"all" | "live" | "test">("all");
  const [search, setSearch] = useState<string>("");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "most_opens" | "failed">("newest");

  // Pagination
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  // Dynamic stream log fetcher: ensures clicking any stream tab loads that stream's emails immediately
  const fetchStreamLogs = async (streamKey: string, rangeKey: string) => {
    setLoadingStream(true);
    try {
      const streamParam = streamKey !== "all" ? `&stream=${streamKey}` : "";
      // The shared window, unless this call deliberately widened to all time.
      const windowPart = windowQuery && rangeKey === range ? windowQuery : `range=${rangeKey}`;
      const res = await fetchWithAuth(`${API_BASE}/api/v1/insights/email-log?${windowPart}${streamParam}&limit=1000`);
      if (res.ok) {
        const data: EmailLogRow[] = await res.json();
        setRows(data);
      }
    } catch (err) {
      console.error("Failed to load stream logs", err);
    } finally {
      setLoadingStream(false);
    }
  };

  // Normalize all rows with guaranteed stream resolution
  const normalizedRows = useMemo(() => {
    return rows.map((r) => {
      const stream = resolveStream(r.purpose, r.stream);
      const streamConfig = STREAM_CONFIG[stream] || STREAM_CONFIG.events;
      return {
        ...r,
        stream,
        stream_label: r.stream_label || streamConfig.label,
      };
    });
  }, [rows]);

  // Ground truth stream counts from PostgreSQL summary (both current window and all-time)
  const { streamCounts, streamAllTimeCounts } = useMemo(() => {
    const counts: Record<string, number> = {
      all: summary?.totals?.total || normalizedRows.length,
    };
    const allTimeCounts: Record<string, number> = {
      all: summary?.totals?.all_time_total || summary?.totals?.total || normalizedRows.length,
    };

    if (summary?.by_stream && summary.by_stream.length > 0) {
      summary.by_stream.forEach((s) => {
        counts[s.stream] = s.total;
        if (s.all_time_total !== undefined) {
          allTimeCounts[s.stream] = s.all_time_total;
        }
      });
    }

    // Fallback: If not present in by_stream, count from in-memory normalizedRows
    Object.keys(STREAM_CONFIG).forEach((k) => {
      if (counts[k] === undefined) {
        counts[k] = normalizedRows.filter((r) => r.stream === k).length;
      }
      if (allTimeCounts[k] === undefined) {
        allTimeCounts[k] = counts[k];
      }
    });

    return { streamCounts: counts, streamAllTimeCounts: allTimeCounts };
  }, [summary, normalizedRows]);

  const handleSelectStream = (key: string) => {
    setActiveStream(key);
    setPurposeFilter("all");
    setAudienceFilter("all");
    setStatusFilter("all");
    setPage(1);

    // If switching to a category that has 0 emails in current range (e.g. Investment Challenge in 7d),
    // but has records all-time, automatically query all-time so the user is never greeted with a blank table!
    const currCount = streamCounts[key] || 0;
    const allTimeCount = streamAllTimeCounts[key] || 0;
    const targetRange = currCount === 0 && allTimeCount > 0 && range !== "all" ? "all" : range;
    fetchStreamLogs(key, targetRange);
  };

  // Robust campaign list: merges pre-cataloged campaigns, Postgres summary, and in-memory rows
  const availablePurposes = useMemo(() => {
    const map = new Map<string, { label: string; stream: string; count: number; allTimeCount: number }>();

    // 1. Seed from predefined catalog for active stream (or all streams)
    const seedStreams = activeStream === "all" ? Object.keys(ALL_CAMPAIGNS_BY_STREAM) : [activeStream];
    seedStreams.forEach((stKey) => {
      const catalog = ALL_CAMPAIGNS_BY_STREAM[stKey] || [];
      catalog.forEach((item) => {
        map.set(item.value, {
          label: item.label,
          stream: stKey,
          count: 0,
          allTimeCount: 0,
        });
      });
    });

    // 2. Incorporate counts from summary.by_purpose
    if (summary?.by_purpose) {
      summary.by_purpose.forEach((p) => {
        const stream = p.stream || resolveStream(p.purpose);
        if (activeStream !== "all" && stream !== activeStream) return;
        const existing = map.get(p.purpose);
        if (existing) {
          existing.count = p.count;
          existing.allTimeCount = p.all_time_count ?? p.count;
          if (p.label) existing.label = p.label;
        } else {
          map.set(p.purpose, {
            label: p.label || p.purpose,
            stream,
            count: p.count,
            allTimeCount: p.all_time_count ?? p.count,
          });
        }
      });
    }

    // 3. Incorporate counts from normalizedRows currently in memory
    normalizedRows.forEach((r) => {
      if (activeStream !== "all" && r.stream !== activeStream) return;
      const existing = map.get(r.purpose);
      if (existing) {
        if (existing.count === 0) existing.count = 1;
      } else {
        map.set(r.purpose, {
          label: r.purpose_label || r.purpose,
          stream: r.stream,
          count: 1,
          allTimeCount: 1,
        });
      }
    });

    return Array.from(map.entries())
      .map(([value, info]) => ({
        value,
        label: info.label,
        stream: info.stream,
        count: info.count,
        allTimeCount: info.allTimeCount,
      }))
      .sort((a, b) => (b.count || b.allTimeCount) - (a.count || a.allTimeCount));
  }, [summary, normalizedRows, activeStream]);

  // Unique Audiences in currently selected stream
  const availableAudiences = useMemo(() => {
    const set = new Set<string>();
    normalizedRows.forEach((r) => {
      if (activeStream !== "all" && r.stream !== activeStream) return;
      if (r.audience) set.add(r.audience);
    });
    return Array.from(set).sort();
  }, [normalizedRows, activeStream]);

  // Active Stream Metrics (ground truth from Postgres summary with in-memory fallback)
  const activeStreamMetrics = useMemo(() => {
    if (activeStream === "all") {
      if (summary?.totals) {
        return {
          total: summary.totals.total || 0,
          allTimeTotal: summary.totals.all_time_total || summary.totals.total || 0,
          sent: summary.totals.sent || 0,
          opened: summary.totals.opened || 0,
          openRate: summary.totals.open_rate || 0,
          clicked: summary.totals.clicked || 0,
          clickRate: summary.totals.click_rate || 0,
          failed: summary.totals.failed || 0,
          draftEdits: summary.draft_edits || draftEdits.length,
        };
      }
    } else if (summary?.by_stream) {
      const match = summary.by_stream.find((s) => s.stream === activeStream);
      if (match) {
        return {
          total: match.total || 0,
          allTimeTotal: match.all_time_total !== undefined ? match.all_time_total : (streamAllTimeCounts[activeStream] || match.total || 0),
          sent: match.sent || 0,
          opened: match.opened || 0,
          openRate: match.open_rate || 0,
          clicked: match.clicked || 0,
          clickRate: match.click_rate || 0,
          failed: match.failed || 0,
          draftEdits: summary.draft_edits || draftEdits.length,
        };
      }
    }

    const streamRows = activeStream === "all" ? normalizedRows : normalizedRows.filter((r) => r.stream === activeStream);
    const total = streamRows.length;
    let sent = 0;
    let opened = 0;
    let clicked = 0;
    let failed = 0;

    streamRows.forEach((r) => {
      if (r.status === "sent" || r.status === "opened") sent++;
      if (r.opened) opened++;
      if (r.clicked) clicked++;
      if (r.status === "error" || (r.status !== "sent" && r.status !== "opened" && r.status !== "skipped" && r.status !== "held")) failed++;
    });

    const openRate = sent > 0 ? Math.round((opened / sent) * 1000) / 10 : 0;
    const clickRate = sent > 0 ? Math.round((clicked / sent) * 1000) / 10 : 0;

    return { total, sent, opened, clicked, failed, openRate, clickRate, draftEdits: summary?.draft_edits || draftEdits.length };
  }, [summary, activeStream, normalizedRows, draftEdits]);

  // Counts for the engagement status segmented control
  const { openedCount, clickedCount, failedCount } = useMemo(() => {
    let opened = 0;
    let clicked = 0;
    let failed = 0;
    normalizedRows.forEach((r) => {
      if (activeStream !== "all" && r.stream !== activeStream) return;
      if (r.opened) opened++;
      if (r.clicked) clicked++;
      if (r.status === "error" || r.bounced || (r.status !== "sent" && r.status !== "opened" && r.status !== "skipped" && r.status !== "held")) failed++;
    });
    return { openedCount: opened, clickedCount: clicked, failedCount: failed };
  }, [normalizedRows, activeStream]);

  // Filter application
  const filteredRows = useMemo(() => {
    return normalizedRows.filter((r) => {
      // 1. Stream filter
      if (activeStream !== "all" && r.stream !== activeStream) return false;

      // 2. Campaign filter
      if (purposeFilter !== "all" && r.purpose !== purposeFilter) return false;

      // 3. Audience filter
      if (audienceFilter !== "all" && r.audience !== audienceFilter) return false;

      // 4. Status filter
      if (statusFilter === "opened" && !r.opened) return false;
      if (statusFilter === "clicked" && !r.clicked) return false;
      if (statusFilter === "unopened" && (r.opened || (r.status !== "sent" && r.status !== "opened"))) return false;
      if (statusFilter === "failed" && (r.status === "sent" || r.status === "opened")) return false;
      if (statusFilter === "bounced" && !r.bounced) return false;
      if (statusFilter === "skipped" && r.status !== "skipped" && r.status !== "held") return false;

      // 5. Test email filter
      const isTestEmail = r.is_test || r.purpose.includes("_test");
      if (testFilter === "live" && isTestEmail) return false;
      if (testFilter === "test" && !isTestEmail) return false;

      // 6. Search query
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const emailMatch = (r.recipient_email || "").toLowerCase().includes(q);
        const nameMatch = (r.recipient_name || "").toLowerCase().includes(q);
        const subjectMatch = (r.subject || "").toLowerCase().includes(q);
        const midMatch = (r.message_id || "").toLowerCase().includes(q);
        const instMatch = ((r.institute_name || r.extra?.institute_name) || "").toLowerCase().includes(q);
        const desigMatch = ((r.designation || r.extra?.designation) || "").toLowerCase().includes(q);
        const attachMatch = ((r.attachment || r.extra?.attachment) || "").toLowerCase().includes(q);
        if (!emailMatch && !nameMatch && !subjectMatch && !midMatch && !instMatch && !desigMatch && !attachMatch) {
          return false;
        }
      }

      return true;
    });
  }, [normalizedRows, activeStream, purposeFilter, statusFilter, audienceFilter, testFilter, search]);

  // Sorted rows
  const sortedRows = useMemo(() => {
    const list = [...filteredRows];
    if (sortBy === "newest") {
      list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    } else if (sortBy === "oldest") {
      list.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    } else if (sortBy === "most_opens") {
      list.sort((a, b) => (b.open_count || (b.opened ? 1 : 0)) - (a.open_count || (a.opened ? 1 : 0)));
    } else if (sortBy === "failed") {
      list.sort((a, b) => {
        const aFail = a.status !== "sent" && a.status !== "opened" ? 1 : 0;
        const bFail = b.status !== "sent" && b.status !== "opened" ? 1 : 0;
        return bFail - aFail;
      });
    }
    return list;
  }, [filteredRows, sortBy]);

  // Paginated Rows
  const totalPages = Math.ceil(sortedRows.length / pageSize) || 1;
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  // Handle Brevo Sync with category-wise scoping support
  const handleBrevoSync = async (specificStream?: string) => {
    setSyncingBrevo(true);
    setSyncMsg(null);
    try {
      const targetStream = specificStream !== undefined ? specificStream : activeStream;
      let url = `${API_BASE}/api/v1/insights/sync-brevo-events?days=${syncDays}`;
      if (targetStream && targetStream !== "all") {
        url += `&stream=${targetStream}`;
      }
      const res = await fetchWithAuth(url, { method: "POST" });
      if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
      const data = await res.json();
      setSyncMsg({ text: data.message });
      onRefresh();
      fetchStreamLogs(activeStream, range);
    } catch (err: any) {
      setSyncMsg({ text: err.message || "Failed to sync Brevo tracking events", error: true });
    } finally {
      setSyncingBrevo(false);
    }
  };

  // Handle Single Email Sync
  const handleSingleEmailSync = async (emailId: string, messageId: string | null, recipient: string) => {
    setSingleSyncingId(emailId);
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/v1/insights/sync-single-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: emailId, message_id: messageId, recipient_email: recipient }),
      });
      if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
      const updated = await res.json();

      setRows((prev) =>
        prev.map((r) => {
          if (r.id === emailId) {
            return {
              ...r,
              status: updated.status,
              opened: updated.opened,
              opened_at: updated.opened_at,
              open_count: updated.open_count,
              clicked: updated.clicked,
              clicked_at: updated.clicked_at,
              click_link: updated.click_link,
              bounced: updated.bounced,
              bounce_reason: updated.bounce_reason,
              delivered: updated.delivered,
              delivered_at: updated.delivered_at,
            };
          }
          return r;
        })
      );

      if (selectedEmail && selectedEmail.id === emailId) {
        setSelectedEmail((prev) => (prev ? { ...prev, ...updated } : null));
      }
    } catch (err: any) {
      alert(`Could not sync email from Brevo: ${err.message}`);
    } finally {
      setSingleSyncingId(null);
    }
  };

  // Copy to clipboard helper
  const handleCopy = (textToCopy: string, id: string) => {
    navigator.clipboard.writeText(textToCopy);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Export to CSV
  const handleExportCSV = () => {
    const headers = [
      "Timestamp",
      "Recipient Name",
      "Recipient Email",
      "Stream",
      "Category",
      "Status",
      "Opened",
      "Open Count",
      "Opened At",
      "Clicked",
      "Clicked At",
      "Clicked Link",
      "Subject",
      "From Email",
      "Institution",
      "Designation",
      "Message ID",
      "Error Detail",
    ];

    const csvRows = sortedRows.map((r) => [
      `"${r.created_at}"`,
      `"${(r.recipient_name || "").replace(/"/g, '""')}"`,
      `"${(r.recipient_email || "").replace(/"/g, '""')}"`,
      `"${(r.stream_label || r.stream || "").replace(/"/g, '""')}"`,
      `"${(r.purpose_label || r.purpose || "").replace(/"/g, '""')}"`,
      `"${r.status}"`,
      `"${r.opened ? "Yes" : "No"}"`,
      r.open_count || (r.opened ? 1 : 0),
      `"${r.opened_at || ""}"`,
      `"${r.clicked ? "Yes" : "No"}"`,
      `"${r.clicked_at || ""}"`,
      `"${(r.click_link || "").replace(/"/g, '""')}"`,
      `"${(r.subject || "").replace(/"/g, '""')}"`,
      `"${(r.from_email || "").replace(/"/g, '""')}"`,
      `"${((r.institute_name || r.extra?.institute_name) || "").replace(/"/g, '""')}"`,
      `"${((r.designation || r.extra?.designation) || "").replace(/"/g, '""')}"`,
      `"${(r.message_id || "").replace(/"/g, '""')}"`,
      `"${(r.error_detail || "").replace(/"/g, '""')}"`,
    ]);

    const csvContent = [headers.join(","), ...csvRows.map((e) => e.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `cmt-email-audit-${activeStream}-${range}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Reset all filters
  const resetFilters = () => {
    setActiveStream("all");
    setPurposeFilter("all");
    setStatusFilter("all");
    setAudienceFilter("all");
    setTestFilter("all");
    setSearch("");
    setPage(1);
  };

  const hasActiveFilters =
    activeStream !== "all" ||
    purposeFilter !== "all" ||
    statusFilter !== "all" ||
    audienceFilter !== "all" ||
    testFilter !== "all" ||
    search.trim() !== "";

  return (
    <div className="space-y-4">
      {/* ── Top Header & Category-Wise Brevo Sync Bar ─────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-xs">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#003d45] flex items-center justify-center text-white shadow-xs shrink-0">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm md:text-base font-bold text-slate-900 tracking-tight font-sans">
                  Email Delivery &amp; Real-Time Engagement Hub
                </h2>
                <span className="px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200/80 rounded-full flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Live Sync
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Audit trail of all member, faculty, candidate, and volunteer emails across DEV &amp; PROD.
              </p>
            </div>
          </div>

          {/* Sync & Export Controls with Category-Wise Sync Option */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Range Preset Selector (hidden when the page's filter bar owns the range) */}
            <div className={`${windowQuery ? "hidden" : "flex"} items-center bg-slate-100 rounded-xl p-0.5 border border-slate-200`}>
              {(["7d", "30d", "90d", "all"] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => {
                    onRangeChange(r);
                    fetchStreamLogs(activeStream, r);
                  }}
                  className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    range === r
                      ? "bg-[#003d45] text-white shadow-xs"
                      : "text-slate-600 hover:text-slate-900 hover:bg-white/60"
                  }`}
                >
                  {r.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Sync Window Selector */}
            <div className="flex items-center bg-slate-50 rounded-xl border border-slate-200 px-2 py-1">
              <span className="text-[11px] font-semibold text-slate-500 mr-1.5">Window:</span>
              <select
                value={syncDays}
                onChange={(e) => setSyncDays(Number(e.target.value))}
                className="bg-transparent text-xs font-bold text-slate-700 outline-none cursor-pointer"
              >
                <option value={7}>7 Days</option>
                <option value={14}>14 Days</option>
                <option value={30}>30 Days</option>
                <option value={60}>60 Days</option>
              </select>
            </div>

            {/* Category-Wise Sync vs All Categories Sync */}
            {activeStream !== "all" ? (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => handleBrevoSync(activeStream)}
                  disabled={syncingBrevo}
                  className="px-3.5 py-1.5 text-xs font-bold rounded-xl bg-[#003d45] text-white hover:bg-[#002d33] transition-all flex items-center gap-1.5 shadow-xs disabled:opacity-60 cursor-pointer disabled:cursor-not-allowed"
                  title={`Sync tracking events for ${STREAM_CONFIG[activeStream]?.label} only`}
                >
                  {syncingBrevo ? (
                    <>
                      <RotateCw className="w-3.5 h-3.5 animate-spin text-white" />
                      <span>Syncing...</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 text-white" />
                      <span>Sync {STREAM_CONFIG[activeStream]?.label}</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => handleBrevoSync("all")}
                  disabled={syncingBrevo}
                  className="px-2.5 py-1.5 text-xs font-semibold rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all cursor-pointer disabled:opacity-60"
                  title="Sync all categories across DEV and PROD"
                >
                  All
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => handleBrevoSync("all")}
                disabled={syncingBrevo}
                className="px-3.5 py-1.5 text-xs font-bold rounded-xl bg-[#003d45] text-white hover:bg-[#002d33] transition-all flex items-center gap-1.5 shadow-xs disabled:opacity-60 cursor-pointer disabled:cursor-not-allowed"
              >
                {syncingBrevo ? (
                  <>
                    <RotateCw className="w-3.5 h-3.5 animate-spin text-white" />
                    <span>Syncing Brevo...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 text-white" />
                    <span>Sync Brevo Now</span>
                  </>
                )}
              </button>
            )}

            <button
              type="button"
              onClick={handleExportCSV}
              className="px-3 py-1.5 text-xs font-semibold rounded-xl bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200 transition-all flex items-center gap-1.5 cursor-pointer"
              title="Download currently filtered table as CSV"
            >
              <Download className="w-3.5 h-3.5 text-slate-500" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {syncMsg && (
          <div
            className={`mt-3 p-3 rounded-xl text-xs font-medium border flex items-center justify-between ${
              syncMsg.error
                ? "bg-red-50 text-red-700 border-red-200"
                : "bg-emerald-50/90 text-emerald-900 border-emerald-200"
            }`}
          >
            <div className="flex items-center gap-2">
              {syncMsg.error ? (
                <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
              ) : (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              )}
              <span>{syncMsg.text}</span>
            </div>
            <button
              type="button"
              onClick={() => setSyncMsg(null)}
              className="text-slate-400 hover:text-slate-600 font-bold ml-3 text-sm cursor-pointer"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {/* ── 1. DOMAIN & STREAM SEGREGATION TABS ───────────────────────── */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between px-1">
          <p className="text-[11px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            Email Domain Streams
          </p>
          <span className="text-[11px] text-slate-400 font-medium">
            {activeStreamMetrics.total.toLocaleString()} total logged in {activeStream === "all" ? "all streams" : STREAM_CONFIG[activeStream]?.label}
          </span>
        </div>

        {/* Stream Pills Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {Object.entries(STREAM_CONFIG).map(([key, config]) => {
            const Icon = config.icon;
            const count = streamCounts[key] || 0;
            const isSelected = activeStream === key;

            return (
              <button
                key={key}
                type="button"
                onClick={() => handleSelectStream(key)}
                className={`flex items-center justify-between p-2.5 rounded-xl border transition-all text-left cursor-pointer ${
                  isSelected
                    ? "bg-[#003d45] text-white border-[#003d45] shadow-sm ring-2 ring-[#007a8a]/30"
                    : "bg-white text-slate-700 border-slate-200/90 hover:border-slate-300 hover:bg-slate-50/80"
                }`}
              >
                <div className="flex items-center gap-2 truncate pr-1">
                  <div
                    className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${
                      isSelected ? "bg-white/20 text-white" : `${config.bg} ${config.color}`
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <span className={`text-xs font-bold truncate ${isSelected ? "text-white" : "text-slate-800"}`}>
                    {config.label}
                  </span>
                </div>
                <span
                  className={`text-[11px] font-mono font-bold px-1.5 py-0.5 rounded-md shrink-0 ${
                    isSelected ? "bg-white/20 text-white" : "bg-slate-100 text-slate-700"
                  }`}
                  title={
                    streamAllTimeCounts[key] > count
                      ? `${count.toLocaleString()} in ${range.toUpperCase()} · ${streamAllTimeCounts[key].toLocaleString()} all-time`
                      : `${count.toLocaleString()} emails`
                  }
                >
                  {count === 0 && streamAllTimeCounts[key] > 0
                    ? `0 (${streamAllTimeCounts[key] >= 1000 ? `${(streamAllTimeCounts[key] / 1000).toFixed(1)}k` : streamAllTimeCounts[key]})`
                    : count.toLocaleString()}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── 2. STREAM KPI METRIC STRIP (CLEAN & COMPACT) ────────────── */}
      <div className="bg-white rounded-xl border border-slate-200/90 p-3.5 shadow-2xs">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
          <div className="px-3 py-1">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">Total In Stream</span>
            <div className="flex items-baseline gap-1.5 mt-0.5">
              <span className="text-xl font-bold text-slate-900 font-mono">
                {activeStreamMetrics.total.toLocaleString()}
              </span>
              {(activeStreamMetrics.allTimeTotal || 0) > activeStreamMetrics.total && (
                <span className="text-[10px] text-slate-400 font-mono" title="Total across all time">
                  ({(activeStreamMetrics.allTimeTotal || 0).toLocaleString()} all-time)
                </span>
              )}
            </div>
          </div>

          <div className="px-3 py-1">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">Sent / Delivered</span>
            <span className="text-xl font-bold text-emerald-600 font-mono mt-0.5 block">
              {activeStreamMetrics.sent.toLocaleString()}
            </span>
          </div>

          <div className="px-3 py-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Open Rate</span>
              <span className="text-[10px] font-bold text-sky-700 bg-sky-50 px-1 py-0.2 rounded">
                {activeStreamMetrics.opened.toLocaleString()}
              </span>
            </div>
            <span className="text-xl font-bold text-sky-700 font-mono mt-0.5 block">
              {activeStreamMetrics.openRate}%
            </span>
          </div>

          <div className="px-3 py-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Click Rate</span>
              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1 py-0.2 rounded">
                {activeStreamMetrics.clicked.toLocaleString()}
              </span>
            </div>
            <span className="text-xl font-bold text-emerald-700 font-mono mt-0.5 block">
              {activeStreamMetrics.clickRate}%
            </span>
          </div>

          <div className="px-3 py-1">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">Failures / Bounces</span>
            <span className={`text-xl font-bold font-mono mt-0.5 block ${activeStreamMetrics.failed > 0 ? "text-red-600" : "text-slate-700"}`}>
              {activeStreamMetrics.failed.toLocaleString()}
            </span>
          </div>

          <div className="px-3 py-1">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">Draft Edits</span>
            <span className="text-xl font-bold text-slate-700 font-mono mt-0.5 block">
              {activeStreamMetrics.draftEdits}
            </span>
          </div>
        </div>
      </div>

      {/* ── 3. RE-DESIGNED "LESS YET BETTER" MULTI-FILTER TOOLBAR ─────── */}
      <div className="bg-white rounded-xl border border-slate-200/90 p-3.5 shadow-2xs space-y-3">
        {/* ROW 1: Wide Search Input + Segmented Engagement Tabs */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          {/* Search bar */}
          <div className="relative flex-1 min-w-[280px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder={`Search ${activeStream === "all" ? "all emails" : STREAM_CONFIG[activeStream]?.label} by recipient, subject, institution, ID...`}
              className="w-full text-xs pl-8.5 pr-7 py-2 bg-slate-50 border border-slate-200/90 rounded-lg text-slate-800 placeholder-slate-400 outline-none focus:border-[#007a8a] focus:bg-white transition-all font-sans"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 font-bold text-xs cursor-pointer p-0.5"
              >
                ✕
              </button>
            )}
          </div>

          {/* Engagement Status Tabs */}
          <div className="flex items-center gap-1 bg-slate-100/80 p-1 rounded-xl border border-slate-200/70 overflow-x-auto">
            {[
              { key: "all", label: `All (${filteredRows.length})` },
              { key: "opened", label: `👁️ Opened (${openedCount})`, activeClass: "bg-sky-600 text-white font-bold" },
              { key: "clicked", label: `🔗 Clicked (${clickedCount})`, activeClass: "bg-emerald-600 text-white font-bold" },
              { key: "failed", label: `⚠️ Issues (${failedCount})`, activeClass: "bg-rose-600 text-white font-bold" },
              { key: "unopened", label: `💤 Unopened`, activeClass: "bg-slate-700 text-white font-bold" },
            ].map((tab) => {
              const isSelected = statusFilter === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    setStatusFilter(tab.key);
                    setPage(1);
                  }}
                  className={`px-3 py-1 text-xs rounded-lg whitespace-nowrap transition-all cursor-pointer font-medium ${
                    isSelected
                      ? tab.activeClass || "bg-[#003d45] text-white font-bold shadow-xs"
                      : "text-slate-600 hover:text-slate-900 hover:bg-white/60"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ROW 2: Contextual Scoped Controls (Campaign, Audience, Mode, Sort) */}
        <div className="flex flex-wrap items-center justify-between gap-2.5 pt-2 border-t border-slate-100">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {/* Scoped Campaign Dropdown */}
            {availablePurposes.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Campaign:</span>
                <select
                  value={purposeFilter}
                  onChange={(e) => {
                    setPurposeFilter(e.target.value);
                    setPage(1);
                  }}
                  className="text-xs py-1 px-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-800 font-medium outline-none focus:border-[#007a8a] cursor-pointer max-w-[220px] truncate"
                >
                  <option value="all">All Campaigns ({availablePurposes.length})</option>
                  {availablePurposes.map((p) => {
                    const displayCount =
                      p.count > 0
                        ? `${p.count}`
                        : p.allTimeCount > 0
                        ? `${p.allTimeCount} all-time`
                        : "0";
                    return (
                      <option key={p.value} value={p.value}>
                        {p.label} ({displayCount})
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {/* Audience Dropdown */}
            {availableAudiences.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Audience:</span>
                <select
                  value={audienceFilter}
                  onChange={(e) => {
                    setAudienceFilter(e.target.value);
                    setPage(1);
                  }}
                  className="text-xs py-1 px-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-800 font-medium outline-none focus:border-[#007a8a] cursor-pointer"
                >
                  <option value="all">All Audiences ({availableAudiences.length})</option>
                  {availableAudiences.map((aud) => (
                    <option key={aud} value={aud}>
                      {aud}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Live vs Test Toggle */}
            <div className="flex items-center bg-slate-100 rounded-lg p-0.5 border border-slate-200">
              <button
                type="button"
                onClick={() => {
                  setTestFilter("all");
                  setPage(1);
                }}
                className={`px-2.5 py-0.5 text-[11px] rounded transition-all cursor-pointer ${
                  testFilter === "all" ? "bg-white text-slate-800 font-bold shadow-2xs" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                All Modes
              </button>
              <button
                type="button"
                onClick={() => {
                  setTestFilter("live");
                  setPage(1);
                }}
                className={`px-2.5 py-0.5 text-[11px] rounded transition-all cursor-pointer ${
                  testFilter === "live" ? "bg-white text-emerald-800 font-bold shadow-2xs" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                Live Sends
              </button>
              <button
                type="button"
                onClick={() => {
                  setTestFilter("test");
                  setPage(1);
                }}
                className={`px-2.5 py-0.5 text-[11px] rounded transition-all cursor-pointer ${
                  testFilter === "test" ? "bg-white text-amber-700 font-bold shadow-2xs" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                Tests Only
              </button>
            </div>
          </div>

          {/* Right side: Sort + Reset */}
          <div className="flex items-center gap-3 ml-auto">
            {hasActiveFilters && (
              <button
                type="button"
                onClick={resetFilters}
                className="text-[11px] font-bold text-rose-600 hover:text-rose-700 cursor-pointer flex items-center gap-1"
              >
                <span>Reset Filters</span>
              </button>
            )}

            <div className="flex items-center gap-1.5">
              <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />
              <select
                value={sortBy}
                onChange={(e: any) => setSortBy(e.target.value)}
                className="bg-transparent text-xs text-slate-700 font-semibold outline-none cursor-pointer"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="most_opens">Most Opened</option>
                <option value="failed">Issues First</option>
              </select>
            </div>
          </div>
        </div>

        {/* Active Filter Badges */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1.5 border-t border-slate-100">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1">Active:</span>

            {activeStream !== "all" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-teal-50 text-teal-800 border border-teal-200">
                Stream: {STREAM_CONFIG[activeStream]?.label}
                <button type="button" onClick={() => handleSelectStream("all")} className="hover:text-teal-950 font-bold cursor-pointer">✕</button>
              </span>
            )}

            {purposeFilter !== "all" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-blue-50 text-blue-800 border border-blue-200">
                Campaign: {availablePurposes.find((p) => p.value === purposeFilter)?.label || purposeFilter}
                <button type="button" onClick={() => setPurposeFilter("all")} className="hover:text-blue-950 font-bold cursor-pointer">✕</button>
              </span>
            )}

            {audienceFilter !== "all" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-purple-50 text-purple-800 border border-purple-200">
                Audience: {audienceFilter}
                <button type="button" onClick={() => setAudienceFilter("all")} className="hover:text-purple-950 font-bold cursor-pointer">✕</button>
              </span>
            )}

            {statusFilter !== "all" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-slate-100 text-slate-800 border border-slate-300">
                Status: {statusFilter}
                <button type="button" onClick={() => setStatusFilter("all")} className="hover:text-slate-950 font-bold cursor-pointer">✕</button>
              </span>
            )}

            {testFilter !== "all" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                Mode: {testFilter === "live" ? "Live" : "Tests"}
                <button type="button" onClick={() => setTestFilter("all")} className="hover:text-amber-950 font-bold cursor-pointer">✕</button>
              </span>
            )}

            {search.trim() && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-slate-100 text-slate-800 border border-slate-300">
                Query: &ldquo;{search}&rdquo;
                <button type="button" onClick={() => setSearch("")} className="hover:text-slate-950 font-bold cursor-pointer">✕</button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── 4. GRANULAR DISPATCH TABLE WITH INLINE ACTIONS ────────────── */}
      <div className="bg-white rounded-xl border border-slate-200/90 overflow-hidden shadow-xs">
        <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-slate-50/70">
          <div>
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-800 flex items-center gap-2">
              <span>Dispatched Emails Trail</span>
              <span className="px-2 py-0.5 text-[10px] font-mono font-bold bg-[#003d45] text-white rounded-full">
                {sortedRows.length.toLocaleString()} matching
              </span>
            </h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Showing page {page} of {totalPages} ({paginatedRows.length} on this page) · Click any row to inspect timeline and payload
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500 font-medium">Rows:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="text-xs py-1 px-2 bg-white border border-slate-200 rounded-lg text-slate-700 outline-none cursor-pointer"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto max-h-[640px]">
          <table className="w-full text-xs">
            <thead className="bg-slate-50/90 text-slate-500 uppercase text-[10px] tracking-widest sticky top-0 z-10 shadow-2xs border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5">Time</th>
                <th className="text-left px-4 py-2.5">Recipient</th>
                <th className="text-left px-4 py-2.5">Stream &amp; Campaign</th>
                <th className="text-left px-4 py-2.5">Subject</th>
                <th className="text-left px-4 py-2.5">Status &amp; Response</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-sans">
              {paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center text-slate-400">
                    <div className="max-w-sm mx-auto space-y-3">
                      <div className="w-12 h-12 rounded-2xl bg-teal-50 border border-teal-200 text-[#007a8a] flex items-center justify-center mx-auto">
                        <Mail className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-800 text-sm">
                          No dispatches found in {range.toUpperCase()} window
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {streamAllTimeCounts[activeStream] > 0
                            ? `${streamAllTimeCounts[activeStream].toLocaleString()} total dispatches exist for ${
                                activeStream === "all" ? "the platform" : STREAM_CONFIG[activeStream]?.label
                              } across all time.`
                            : "Try clearing active filters or selecting another stream."}
                        </p>
                      </div>

                      <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                        {streamAllTimeCounts[activeStream] > 0 && range !== "all" && (
                          <button
                            type="button"
                            onClick={() => {
                              onRangeChange("all");
                              fetchStreamLogs(activeStream, "all");
                            }}
                            className="px-3.5 py-1.5 text-xs font-bold rounded-xl bg-[#003d45] text-white hover:bg-[#002d33] transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
                          >
                            <RotateCw className="w-3.5 h-3.5" />
                            <span>View All Time ({streamAllTimeCounts[activeStream].toLocaleString()})</span>
                          </button>
                        )}
                        {hasActiveFilters && (
                          <button
                            type="button"
                            onClick={resetFilters}
                            className="px-3 py-1.5 text-xs font-semibold rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all cursor-pointer"
                          >
                            Clear all filters
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedRows.map((r) => {
                  const isOpened = r.opened;
                  const isClicked = r.clicked;
                  const isFailed = r.status !== "sent" && r.status !== "opened" && r.status !== "skipped" && r.status !== "held";
                  const inst = r.institute_name || r.extra?.institute_name;
                  const desig = r.designation || r.extra?.designation;
                  const isTest = r.is_test || r.purpose.includes("_test");
                  const streamConfig = STREAM_CONFIG[r.stream] || STREAM_CONFIG.events;

                  return (
                    <tr
                      key={r.id}
                      onClick={() => setSelectedEmail(r)}
                      className={`cursor-pointer transition-colors hover:bg-teal-50/30 ${
                        isFailed ? "bg-red-50/30" : isOpened ? "bg-sky-50/10" : ""
                      }`}
                    >
                      {/* Time */}
                      <td className="px-4 py-3 text-slate-500 font-mono whitespace-nowrap align-top">
                        <span className="font-medium text-slate-700 block">{relTime(r.created_at)}</span>
                        <span className="text-[10px] text-slate-400 block">
                          {new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </td>

                      {/* Recipient */}
                      <td className="px-4 py-3 align-top max-w-[280px]">
                        <div className="flex items-start gap-2.5">
                          <div className="w-7 h-7 rounded-lg bg-slate-100 text-slate-700 font-bold text-[11px] flex items-center justify-center shrink-0 uppercase border border-slate-200">
                            {(r.recipient_name || r.recipient_email || "U").charAt(0)}
                          </div>
                          <div className="truncate">
                            <div className="font-bold text-slate-900 truncate">
                              {r.recipient_name || r.recipient_email}
                            </div>
                            <div className="text-[11px] text-slate-500 font-mono truncate">
                              {r.recipient_email}
                            </div>
                            {inst && (
                              <div className="inline-flex items-center gap-1 text-[10px] font-medium text-[#007a8a] bg-teal-50 px-1.5 py-0.2 rounded mt-0.5 truncate max-w-full">
                                <Building className="w-2.5 h-2.5 shrink-0" />
                                <span className="truncate">{inst}</span>
                              </div>
                            )}
                            {desig && (
                              <div className="text-[10px] text-slate-400 truncate mt-0.5">
                                {desig}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Stream & Category */}
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`px-2 py-0.5 text-[10px] font-bold rounded-md uppercase tracking-wider ${
                                streamConfig.bg
                              } ${streamConfig.color} border ${streamConfig.border}`}
                            >
                              {r.stream_label || streamConfig.label}
                            </span>
                            {isTest && (
                              <span className="px-1.5 py-0.2 text-[9px] font-black uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 rounded">
                                Test
                              </span>
                            )}
                          </div>
                          <div className="font-semibold text-slate-800 text-xs">
                            {r.purpose_label || r.purpose}
                          </div>
                          {r.audience && (
                            <div className="text-[10px] text-slate-400 font-mono">
                              audience: {r.audience}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Subject */}
                      <td className="px-4 py-3 align-top max-w-[240px]">
                        <div className="text-slate-700 font-medium truncate" title={r.subject || ""}>
                          {r.subject || "—"}
                        </div>
                        {r.event_title && (
                          <div className="flex items-center gap-1 text-[10px] text-[#007a8a] truncate mt-1">
                            <Calendar className="w-3 h-3 shrink-0" />
                            <span className="truncate">{r.event_title}</span>
                          </div>
                        )}
                        {r.attachment && (
                          <div className="flex items-center gap-1 text-[10px] text-slate-500 truncate mt-1">
                            <Paperclip className="w-2.5 h-2.5 shrink-0" />
                            <span className="truncate font-mono">{r.attachment}</span>
                          </div>
                        )}
                      </td>

                      {/* Status & Response */}
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span
                              className={`px-2 py-0.5 rounded font-mono font-bold text-[10px] uppercase tracking-wider ${
                                r.status === "sent" || r.status === "opened"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : r.status === "skipped" || r.status === "held"
                                  ? "bg-amber-50 text-amber-700 border border-amber-200"
                                  : "bg-red-50 text-red-700 border border-red-200"
                              }`}
                            >
                              {r.status}
                            </span>

                            {isOpened && (
                              <span
                                className="px-2 py-0.5 text-[10px] font-bold rounded bg-sky-50 text-sky-700 border border-sky-200 flex items-center gap-1"
                                title={r.opened_at ? `Opened at ${new Date(r.opened_at).toLocaleString()}` : "Opened"}
                              >
                                <Eye className="w-3 h-3 text-sky-600" />
                                <span>Opened{r.open_count && r.open_count > 1 ? ` (${r.open_count})` : ""}</span>
                              </span>
                            )}

                            {isClicked && (
                              <span
                                className="px-2 py-0.5 text-[10px] font-bold rounded bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1"
                                title={r.clicked_at ? `Clicked at ${new Date(r.clicked_at).toLocaleString()}` : "Clicked"}
                              >
                                <MousePointerClick className="w-3 h-3 text-emerald-600" />
                                <span>Clicked</span>
                              </span>
                            )}

                            {r.bounced && (
                              <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-rose-50 text-rose-700 border border-rose-200">
                                Bounced
                              </span>
                            )}
                          </div>

                          {r.opened_at && (
                            <div className="text-[10px] text-slate-400">
                              Opened {relTime(r.opened_at)}
                            </div>
                          )}

                          {r.error_detail && (
                            <div className="text-[10px] text-red-600 truncate max-w-[200px]" title={r.error_detail}>
                              {r.error_detail}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                        <div
                          className="flex items-center justify-end gap-1.5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => setSelectedEmail(r)}
                            className="p-1.5 rounded-lg bg-slate-100 hover:bg-[#003d45] hover:text-white text-slate-600 transition-colors cursor-pointer"
                            title="Inspect details and timeline"
                          >
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleCopy(r.recipient_email, `email-${r.id}`)}
                            className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors cursor-pointer"
                            title="Copy email address"
                          >
                            {copiedId === `email-${r.id}` ? (
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>

                          <button
                            type="button"
                            disabled={singleSyncingId === r.id}
                            onClick={() => handleSingleEmailSync(r.id, r.message_id, r.recipient_email)}
                            className="p-1.5 rounded-lg bg-slate-100 hover:bg-teal-50 hover:text-[#007a8a] text-slate-600 transition-colors disabled:opacity-50 cursor-pointer"
                            title="Sync live tracking from Brevo"
                          >
                            <RotateCw
                              className={`w-3.5 h-3.5 ${singleSyncingId === r.id ? "animate-spin text-[#007a8a]" : ""}`}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-slate-50/50">
          <div className="text-xs text-slate-500">
            Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, sortedRows.length)} of{" "}
            {sortedRows.length.toLocaleString()} entries
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-white border border-slate-200 text-slate-700 disabled:opacity-40 hover:bg-slate-50 cursor-pointer disabled:cursor-not-allowed flex items-center gap-1"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              <span>Previous</span>
            </button>
            <span className="text-xs font-bold text-slate-700 px-2 font-mono">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-white border border-slate-200 text-slate-700 disabled:opacity-40 hover:bg-slate-50 cursor-pointer disabled:cursor-not-allowed flex items-center gap-1"
            >
              <span>Next</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* ── 5. SLIDE-OVER DETAIL INSPECTION DRAWER ────────────────────── */}
      {selectedEmail && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-slate-900/40 backdrop-blur-xs flex justify-end animate-in fade-in duration-200">
          <div
            className="w-full max-w-xl bg-white h-full shadow-2xl overflow-y-auto flex flex-col border-l border-slate-200 animate-in slide-in-from-right duration-250"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div className="p-5 border-b border-slate-200 bg-slate-50/80 sticky top-0 z-10 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase tracking-wider ${
                      STREAM_CONFIG[selectedEmail.stream || "general"]?.bg || "bg-slate-100"
                    } ${STREAM_CONFIG[selectedEmail.stream || "general"]?.color || "text-slate-700"} border ${
                      STREAM_CONFIG[selectedEmail.stream || "general"]?.border || "border-slate-200"
                    }`}
                  >
                    {selectedEmail.stream_label || selectedEmail.stream || "General"}
                  </span>
                  <span className="font-mono text-[11px] text-slate-400">
                    {relTime(selectedEmail.created_at)}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mt-1 font-sans">
                  {selectedEmail.recipient_name || selectedEmail.recipient_email}
                </h3>
                <p className="text-xs text-slate-500 font-mono">{selectedEmail.recipient_email}</p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedEmail(null)}
                  className="w-8 h-8 rounded-xl bg-slate-200/70 hover:bg-slate-300 text-slate-700 flex items-center justify-center font-bold text-sm transition-colors cursor-pointer"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Drawer Body */}
            <div className="p-6 space-y-6 flex-1">
              {/* Quick Actions Bar */}
              <div className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200/80">
                <button
                  type="button"
                  disabled={singleSyncingId === selectedEmail.id}
                  onClick={() =>
                    handleSingleEmailSync(
                      selectedEmail.id,
                      selectedEmail.message_id,
                      selectedEmail.recipient_email
                    )
                  }
                  className="px-3 py-1.5 text-xs font-bold rounded-lg bg-[#003d45] text-white hover:bg-[#002b31] transition-all flex items-center gap-1.5 shadow-2xs disabled:opacity-50 cursor-pointer"
                >
                  <RotateCw
                    className={`w-3.5 h-3.5 ${singleSyncingId === selectedEmail.id ? "animate-spin" : ""}`}
                  />
                  <span>Sync Brevo Now</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleCopy(selectedEmail.recipient_email, "drawer-email")}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  {copiedId === "drawer-email" ? (
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  <span>Copy Address</span>
                </button>

                <a
                  href={`mailto:${selectedEmail.recipient_email}`}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 transition-colors flex items-center gap-1.5"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>Send Direct Email</span>
                </a>

                <button
                  type="button"
                  onClick={() => {
                    setSearch(selectedEmail.recipient_email);
                    setSelectedEmail(null);
                  }}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-slate-200 hover:bg-slate-100 text-[#007a8a] transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  <Search className="w-3.5 h-3.5" />
                  <span>Filter this Recipient</span>
                </button>
              </div>

              {/* Engagement Timeline */}
              <div className="space-y-3">
                <h4 className="text-xs font-black uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  Delivery &amp; Engagement Timeline
                </h4>

                <div className="bg-white border border-slate-200 rounded-xl p-4 divide-y divide-slate-100">
                  {/* Step 1: Dispatched */}
                  <div className="pb-3 flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3 h-3" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-800">
                        Email Dispatched to Brevo Relay
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                        {new Date(selectedEmail.created_at).toLocaleString()}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        Sender: <span className="font-mono">{selectedEmail.from_email || "admin@cmtassociation.org"}</span>
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Delivered */}
                  <div className="py-3 flex items-start gap-3">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                        selectedEmail.delivered || selectedEmail.status === "sent" || selectedEmail.opened
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-slate-100 text-slate-400 border border-slate-200"
                      }`}
                    >
                      <Check className="w-3 h-3" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-800">
                        Delivered to Recipient Server
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                        {selectedEmail.delivered_at
                          ? new Date(selectedEmail.delivered_at).toLocaleString()
                          : "Delivered via transactional SMTP relay"}
                      </div>
                    </div>
                  </div>

                  {/* Step 3: Open status */}
                  <div className="py-3 flex items-start gap-3">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                        selectedEmail.opened
                          ? "bg-sky-50 text-sky-700 border border-sky-200"
                          : "bg-slate-100 text-slate-400 border border-slate-200"
                      }`}
                    >
                      <Eye className="w-3 h-3" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-800 flex items-center gap-2">
                        <span>Email Opened</span>
                        {selectedEmail.opened && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] bg-sky-100 text-sky-800 font-bold font-mono">
                            {selectedEmail.open_count || 1} open event(s)
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                        {selectedEmail.opened_at
                          ? `${new Date(selectedEmail.opened_at).toLocaleString()} (${relTime(selectedEmail.opened_at)})`
                          : "Not opened yet by recipient"}
                      </div>
                    </div>
                  </div>

                  {/* Step 4: Clicked links */}
                  <div className="pt-3 flex items-start gap-3">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                        selectedEmail.clicked
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-slate-100 text-slate-400 border border-slate-200"
                      }`}
                    >
                      <MousePointerClick className="w-3 h-3" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-800">Link Clicks Recorded</div>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                        {selectedEmail.clicked_at
                          ? `Clicked ${relTime(selectedEmail.clicked_at)} (${new Date(selectedEmail.clicked_at).toLocaleString()})`
                          : "No link clicks recorded yet"}
                      </div>
                      {selectedEmail.click_link && (
                        <div className="mt-1.5 p-2 rounded-lg bg-slate-50 border border-slate-200 break-all text-[11px]">
                          <span className="font-semibold text-slate-600 block mb-0.5">Clicked URL:</span>
                          <a
                            href={selectedEmail.click_link}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#007a8a] hover:underline flex items-center gap-1"
                          >
                            <span>{selectedEmail.click_link}</span>
                            <ExternalLink className="w-3 h-3 shrink-0" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Bounced / Error Warning Card */}
              {(selectedEmail.bounced || selectedEmail.error_detail) && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl space-y-1.5">
                  <div className="flex items-center gap-2 text-xs font-bold text-red-800">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    <span>Delivery Failure / Bounce Notice</span>
                  </div>
                  {selectedEmail.bounce_reason && (
                    <div className="text-xs text-red-700">
                      <b>Bounce Reason:</b> {selectedEmail.bounce_reason}
                    </div>
                  )}
                  {selectedEmail.error_detail && (
                    <div className="text-xs text-red-700 font-mono whitespace-pre-wrap break-all">
                      <b>Error detail:</b> {selectedEmail.error_detail}
                    </div>
                  )}
                </div>
              )}

              {/* Campaign & Context Details */}
              <div className="space-y-3">
                <h4 className="text-xs font-black uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                  <Building className="w-3.5 h-3.5 text-slate-400" />
                  Context &amp; Campaign Metadata
                </h4>

                <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2.5 text-xs">
                  <div className="grid grid-cols-3 gap-2 py-1 border-b border-slate-100">
                    <span className="text-slate-400 font-medium">Category / Purpose:</span>
                    <span className="col-span-2 font-semibold text-slate-800">
                      {selectedEmail.purpose_label} ({selectedEmail.purpose})
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 py-1 border-b border-slate-100">
                    <span className="text-slate-400 font-medium">Subject Line:</span>
                    <span className="col-span-2 text-slate-700 font-medium">
                      {selectedEmail.subject || "—"}
                    </span>
                  </div>

                  {selectedEmail.event_title && (
                    <div className="grid grid-cols-3 gap-2 py-1 border-b border-slate-100">
                      <span className="text-slate-400 font-medium">Event / Meeting:</span>
                      <span className="col-span-2 font-semibold text-[#007a8a]">
                        {selectedEmail.event_title}
                        {selectedEmail.group_name && ` · ${selectedEmail.group_name}`}
                      </span>
                    </div>
                  )}

                  {(selectedEmail.institute_name || selectedEmail.extra?.institute_name) && (
                    <div className="grid grid-cols-3 gap-2 py-1 border-b border-slate-100">
                      <span className="text-slate-400 font-medium">Institution:</span>
                      <span className="col-span-2 font-semibold text-slate-800">
                        {selectedEmail.institute_name || selectedEmail.extra?.institute_name}
                      </span>
                    </div>
                  )}

                  {(selectedEmail.designation || selectedEmail.extra?.designation) && (
                    <div className="grid grid-cols-3 gap-2 py-1 border-b border-slate-100">
                      <span className="text-slate-400 font-medium">Designation:</span>
                      <span className="col-span-2 text-slate-700">
                        {selectedEmail.designation || selectedEmail.extra?.designation}
                      </span>
                    </div>
                  )}

                  {(selectedEmail.attachment || selectedEmail.extra?.attachment) && (
                    <div className="grid grid-cols-3 gap-2 py-1 border-b border-slate-100">
                      <span className="text-slate-400 font-medium">Attachment PDF:</span>
                      <span className="col-span-2 font-mono text-slate-700 break-all">
                        {selectedEmail.attachment || selectedEmail.extra?.attachment}
                      </span>
                    </div>
                  )}

                  {selectedEmail.message_id && (
                    <div className="grid grid-cols-3 gap-2 py-1 border-b border-slate-100">
                      <span className="text-slate-400 font-medium">Brevo Message ID:</span>
                      <span className="col-span-2 font-mono text-[11px] text-slate-600 break-all flex items-center justify-between">
                        <span>{selectedEmail.message_id}</span>
                        <button
                          type="button"
                          onClick={() => handleCopy(selectedEmail.message_id || "", "mid-copy")}
                          className="text-[#007a8a] hover:underline font-bold text-[10px] ml-2 shrink-0 cursor-pointer"
                        >
                          {copiedId === "mid-copy" ? "Copied!" : "Copy"}
                        </button>
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Raw JSON Payload Inspector */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-700">
                    Audit Payload JSON
                  </h4>
                  <button
                    type="button"
                    onClick={() =>
                      handleCopy(JSON.stringify(selectedEmail.extra || {}, null, 2), "payload-copy")
                    }
                    className="text-[11px] text-[#007a8a] font-semibold hover:underline cursor-pointer"
                  >
                    {copiedId === "payload-copy" ? "Copied JSON!" : "Copy Raw JSON"}
                  </button>
                </div>
                <pre className="p-3.5 bg-slate-900 text-slate-100 rounded-xl text-[11px] font-mono overflow-x-auto max-h-56 leading-relaxed">
                  {JSON.stringify(selectedEmail.extra || {}, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 6. DRAFT EDITS TABLE ────────────────────────────────────── */}
      {draftEdits.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200/90 overflow-hidden shadow-2xs">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/70">
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-800">
              Recent Staff Draft Edits ({draftEdits.length})
            </h3>
            <p className="text-[10px] text-slate-400 mt-0.5">
              Audit log of changes made by staff to event invitation copy, speaker notes, and member emails.
            </p>
          </div>
          <div className="overflow-x-auto max-h-60">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2">Time</th>
                  <th className="text-left px-4 py-2">Edited by</th>
                  <th className="text-left px-4 py-2">Field</th>
                  <th className="text-left px-4 py-2">Meeting ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {draftEdits.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-500 font-mono whitespace-nowrap">
                      {relTime(d.created_at)}
                    </td>
                    <td className="px-4 py-2 font-medium text-slate-800">
                      {d.user?.name || d.user?.email || "Staff"}
                    </td>
                    <td className="px-4 py-2 font-mono text-[#007a8a]">
                      {d.field_path}
                    </td>
                    <td className="px-4 py-2 font-mono text-slate-500">
                      {d.meeting_id.slice(0, 8)}...
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
